'use client';

import { useCallback } from 'react';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { useUpdateCurrentUserMutation } from 'src/api/redux';
import { useAppDispatch } from 'src/redux/hooks';
import {
  setCurrentTerritory,
  setHeatMap,
} from 'src/redux/slices/discoveryMapSlice';

/**
 * Returns a function that makes a territory (or no territory, when passed
 * `null`) the Discovery map's current territory and saves it as the user's
 * `last_territory`. Updating `user.last_territory` matters because
 * `useDiscoveryTerritory` re-applies it when Discovery mounts.
 */
const useSelectDiscoveryTerritory = () => {
  const dispatch = useAppDispatch();
  const log = useLogging();
  const { setUser } = useAuth();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();

  return useCallback(
    (territory: MedScout.Territory | null) => {
      dispatch(setCurrentTerritory(territory));
      dispatch(setHeatMap(false));
      setUser((oldUser) => ({
        ...oldUser,
        last_territory: territory,
      }));

      return updateCurrentUser({ last_territory_id: territory?.id ?? null })
        .unwrap()
        .catch((error) => {
          log.exception(error, {
            tags: {
              action: territory ? 'set_last_territory' : 'clear_last_territory',
              territoryId: territory?.id,
            },
          });
        });
    },
    [dispatch, log, setUser, updateCurrentUser]
  );
};

export default useSelectDiscoveryTerritory;
